import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Loader2, PenLine } from "lucide-react";
import { api } from "@/lib/api";
import { RESEARCH } from "@/constants/testIds";
import { useAuth } from "@/lib/auth";
import IdeasList from "@/components/IdeasList";
import ResearchSchedulePanel from "@/components/ResearchSchedulePanel";

export default function ResearchPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [guidelines, setGuidelines] = useState("");
  const [research, setResearch] = useState(null);
  const activeProperty = (user?.properties || []).find(
    (p) => p.id === user?.active_property_id
  );

  const { data: writers = [] } = useQuery({
    queryKey: ["writers"],
    queryFn: api.listWriters,
  });

  const researchMutation = useMutation({
    mutationFn: async (g) => {
      const initial = await api.createResearch({ guidelines: g });
      let current = initial;
      const startedAt = Date.now();
      while (current.status === "pending") {
        if (Date.now() - startedAt > 5 * 60 * 1000) {
          throw new Error("Research timed out after 5 minutes");
        }
        await new Promise((r) => setTimeout(r, 3000));
        current = await api.getResearch(initial.id);
      }
      if (current.status === "error") {
        throw new Error(current.error || "Research failed");
      }
      return current;
    },
    onSuccess: (data) => {
      setResearch(data);
      qc.invalidateQueries({ queryKey: ["past-runs"] });
      toast.success(`${data.ideas.length} story ideas ready`);
    },
    onError: (e) =>
      toast.error(e?.message || e?.response?.data?.detail || "Research failed"),
  });

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-[var(--border)] bg-white sticky top-0 z-10">
        <div className="px-4 sm:px-10 py-5 sm:py-6">
          <div className="overline">
            Newsroom · Research{activeProperty ? ` · ${activeProperty.name}` : ""}
          </div>
          <h1 className="font-serif-display text-3xl sm:text-4xl tracking-tight leading-tight mt-1">
            What&rsquo;s the story today?
          </h1>
          <p className="text-sm text-[var(--ink-muted)] mt-2 max-w-2xl">
            The desk uses your editorial brief (set in Settings) to scan
            configured sources and return 10&ndash;15 story angles. Optional
            per-run guidelines below let you steer today&rsquo;s search without
            changing your standing brief.
          </p>
        </div>
      </div>

      <div className="px-4 sm:px-10 py-6 sm:py-8 flex-1 overflow-auto">
        <ResearchSchedulePanel />

        <div className="bg-white border border-[var(--border)] mb-8">
          <div className="px-5 py-3 border-b border-[var(--border)] flex items-center gap-2">
            <PenLine className="w-4 h-4 text-[var(--accent)]" />
            <span className="overline">Today&rsquo;s guidelines · optional</span>
          </div>
          <textarea
            data-testid={RESEARCH.promptInput}
            value={guidelines}
            onChange={(e) => setGuidelines(e.target.value)}
            rows={4}
            className="w-full resize-none px-5 py-4 text-sm leading-relaxed bg-white outline-none"
            placeholder="Any specific research guidelines you need me to follow today? (Leave blank to use your standing editorial brief.)"
          />
          <div className="px-5 py-3 border-t border-[var(--border)] flex items-center justify-between">
            <span className="text-xs text-[var(--ink-muted)] font-mono-display">
              {new Date().toDateString()}
            </span>
            <button
              data-testid={RESEARCH.submitButton}
              data-ai="true"
              onClick={() => researchMutation.mutate(guidelines)}
              disabled={researchMutation.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate-900)] text-white text-sm hover:bg-black disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {researchMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Scouting…
                </>
              ) : (
                <>
                  <Search className="w-4 h-4" />
                  Run research
                </>
              )}
            </button>
          </div>
        </div>

        {researchMutation.isPending && (
          <div
            data-testid={RESEARCH.loading}
            className="bg-white border border-[var(--border)] py-14 text-center"
          >
            <div className="text-[var(--accent)]">
              <span className="loading-dot"></span>
              <span className="loading-dot"></span>
              <span className="loading-dot"></span>
            </div>
            <div className="font-serif-display text-xl mt-4">Reading the wires…</div>
            <div className="text-xs text-[var(--ink-muted)] mt-2 font-mono-display">
              Scanning configured sources · usually 60-120 seconds
            </div>
          </div>
        )}

        {!researchMutation.isPending && !research && (
          <div
            data-testid={RESEARCH.emptyState}
            className="bg-white border border-[var(--border)] py-16 text-center"
          >
            <div className="font-serif-display text-2xl mb-2">No stories yet</div>
            <div className="text-sm text-[var(--ink-muted)] max-w-md mx-auto">
              Hit <em>Run research</em> to get today&rsquo;s ideas, or browse past
              runs in the sidebar.
            </div>
          </div>
        )}

        {research && !researchMutation.isPending && (
          <IdeasList
            research={research}
            writers={writers}
            // ResearchPage holds `research` in local component state
            // (not React Query), so `qc.refetchQueries` inside
            // IdeasList would be a no-op here. This callback closes
            // that loop: whenever a mutation persists new sources /
            // do-not-cover flags on the server, we pull a fresh
            // research doc so `idea.source_urls` and the rest of the
            // UI reflect the change. Without this, the "added source
            // vanishes until refresh" race hit this page.
            onResearchStale={async () => {
              try {
                const fresh = await api.getResearch(research.id);
                setResearch(fresh);
              } catch (e) {
                // Non-fatal — IdeasList keeps the URL in local extras
                // as a backstop, so nothing is lost visually.
                // eslint-disable-next-line no-console
                console.warn("ResearchPage refresh failed", e);
              }
            }}
          />
        )}
      </div>
    </div>
  );
}
